import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { initials, money2 } from '../lib/format.js';
import { IconSearch } from '../components/icons.js';

/**
 * Global search — one field that searches both clients and inventory, so a rep
 * doesn't have to know which tab to be on. Reachable from Home.
 */
export function SearchScreen() {
  const [q, setQ] = useState('');
  const [activeQ, setActiveQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setActiveQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const enabled = activeQ.length >= 2;
  const clients = useQuery({
    queryKey: ['search-clients', activeQ],
    queryFn: ({ signal }) => api.customers.search({ q: activeQ }, signal),
    enabled,
  });
  const items = useQuery({
    queryKey: ['search-items', activeQ],
    queryFn: ({ signal }) => api.inventory.search({ q: activeQ, limit: 15 }, signal),
    enabled,
  });

  const clientHits = clients.data ?? [];
  const itemHits = items.data ?? [];
  const nothing = enabled && clients.isSuccess && items.isSuccess && clientHits.length === 0 && itemHits.length === 0;

  return (
    <>
      <div className="topbar">
        <Link to="/" className="back-btn">‹ Home</Link>
      </div>
      <div className="search-bar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients or items"
            aria-label="Search clients or items"
          />
        </div>
      </div>

      {!enabled && (
        <div className="kd-muted" style={{ padding: 18 }}>Type at least 2 characters to search clients and inventory.</div>
      )}
      {nothing && <div className="kd-muted" style={{ padding: 18 }}>No clients or items match “{activeQ}”.</div>}

      {clientHits.length > 0 && (
        <>
          <div className="section-title">Clients · {clientHits.length}</div>
          {clientHits.slice(0, 10).map((c) => (
            <Link key={c.spireId} to="/clients/$id" params={{ id: String(c.spireId) }} className="customer-row">
              <div className="avatar">{initials(c.name)}</div>
              <div className="body">
                <div className="name">{c.name}</div>
                <div className="meta">
                  {[c.address.city, c.address.provState].filter(Boolean).join(', ') || c.customerNo}
                  {c.hold ? ' · A/R hold' : ''}
                </div>
              </div>
            </Link>
          ))}
        </>
      )}

      {itemHits.length > 0 && (
        <>
          <div className="section-title">Items · {itemHits.length}</div>
          {itemHits.slice(0, 15).map((it) => (
            <Link key={it.spireId} to="/inventory/$id" params={{ id: String(it.spireId) }} className="customer-row">
              <div className="avatar" style={{ borderRadius: 10, background: 'var(--kd-bg)', color: 'var(--kd-text-2)', fontSize: 11 }}>
                {it.whseCode}
              </div>
              <div className="body">
                <div className="name">{it.partNo}</div>
                <div className="meta">{it.description ?? ''}</div>
              </div>
              <div className="right">
                <div className="strong">{money2(it.defaultSellPrice)}</div>
                <div>{Number(it.availableQty) > 0 ? `${it.availableQty} avail` : 'out'}</div>
              </div>
            </Link>
          ))}
        </>
      )}
    </>
  );
}
